import { FormControl, Text } from '@chakra-ui/react';
import { Form, Formik, FormikHelpers } from 'formik';
import NextLink from 'next/link';
import React from 'react';

import FacebookAuthButton from '~/components/AuthenticationLayout/components/FacebookAuthButton';
import GoogleAuthButton from '~/components/AuthenticationLayout/components/GoogleAuthButton';
import MicrosoftAuthButton from '~/components/AuthenticationLayout/components/MicrosoftAuthButton';
import Button from '~/components/Button';
import EmailInput from '~/components/EmailInput';
import FormLabel from '~/components/FormLabel';
import Link from '~/components/Link';
import PasswordInput from '~/components/PasswordInput';
import { FEATURE_FLAGS } from '~/constants/featureFlags';
import { INVITATON_TYPES } from '~/constants/invitationType';
import { PLATFORMS } from '~/constants/platforms';
import { routes } from '~/constants/routes';
import { SSO_PLATFORMS } from '~/constants/ssoPlatforms';
import { AlertError } from '~/contexts/alert/alertFeedBackError';
import { useFeature } from '~/hooks/useFeature';
import { setButtonHeapTrackId } from '~/utils/setButtonHeapTrackId';

import { FormValues } from './SignUpScreen.vm';

interface Props {
  email?: string;
  heading?: React.ReactNode;
  isInvitation?: boolean;
  onSubmit: (fields: FormValues, actions: FormikHelpers<FormValues>) => void;
  googleAuthUrl?: string | AlertError;
  microsoftAuthUrl?: string | AlertError;
  facebookAuthUrl?: string | AlertError;
  platform: PLATFORMS;
  invitationType?: INVITATON_TYPES;
  adminInvitationEmail?: string | null;
}

export const SignUpScreenComponent: React.FC<Props> = ({
  email = '',
  heading,
  isInvitation,
  onSubmit,
  googleAuthUrl,
  microsoftAuthUrl,
  facebookAuthUrl,
  platform,
  invitationType,
  adminInvitationEmail,
}) => {
  const { isEnabled: isFacebookAuthEnabled } = useFeature(
    FEATURE_FLAGS.facebookAuth
  );
  return (
    <>
      {heading}

      <Formik
        initialValues={{ email, password: '' }}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({
          errors,
          handleSubmit,
          handleBlur,
          handleChange,
          isSubmitting,
          initialValues,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <FormControl id="email" marginBottom={4}>
                <FormLabel
                  color="primary.600"
                  fontWeight="medium"
                  fontSize="md"
                >
                  Email address
                </FormLabel>
                <EmailInput
                  defaultValue={
                    email || adminInvitationEmail || initialValues.email
                  }
                  isDisabled={isInvitation || isSubmitting}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder="Email"
                  size="lg"
                  type="email"
                  width="100%"
                  isRequired
                  autoFocus
                />
              </FormControl>

              <FormControl
                id="password"
                isInvalid={!!errors.password}
                marginBottom={6}
              >
                <FormLabel
                  color="primary.600"
                  fontWeight="medium"
                  fontSize="md"
                >
                  New Password
                </FormLabel>
                <PasswordInput
                  defaultValue={initialValues.password}
                  isDisabled={isSubmitting}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder="Password"
                  size="lg"
                  width="100%"
                  isRequired
                  pattern="^.{8,}$"
                  errorTextPattern="Password must be at least 8 characters"
                  errorTextRequired="Please enter your password"
                  errors={errors.password}
                />
              </FormControl>

              <Button
                marginBottom={3}
                colorScheme="primary"
                width="100%"
                fontSize="md"
                isLoading={isSubmitting}
                size="lg"
                type="submit"
                track-id={setButtonHeapTrackId(
                  'signup',
                  isInvitation,
                  platform,
                  undefined,
                  invitationType
                )}
              >
                Sign up
              </Button>
            </Form>
          );
        }}
      </Formik>
      <div>
        <Text
          color="primary.400"
          fontSize="sm"
          textAlign="center"
          marginBottom={3}
        >
          Or
        </Text>
        <GoogleAuthButton
          googleAuthUrl={googleAuthUrl}
          trackId={setButtonHeapTrackId(
            'signup',
            isInvitation,
            platform,
            SSO_PLATFORMS.google,
            invitationType
          )}
        />
        <MicrosoftAuthButton
          microsoftAuthUrl={microsoftAuthUrl}
          trackId={setButtonHeapTrackId(
            'signup',
            isInvitation,
            platform,
            SSO_PLATFORMS.microsoft,
            invitationType
          )}
        />
        {isFacebookAuthEnabled && (
          <FacebookAuthButton
            facebookAuthUrl={facebookAuthUrl}
            trackId={setButtonHeapTrackId(
              'signup',
              isInvitation,
              platform,
              SSO_PLATFORMS.facebook,
              invitationType
            )}
          />
        )}
        <Text color="primary.400" fontSize="sm" textAlign="center">
          Have an account?{' '}
          <Link
            as={NextLink}
            href={
              platform === PLATFORMS.foam
                ? routes.foam.login
                : routes.echo.login
            }
            fontWeight="medium"
            color={platform === PLATFORMS.foam ? 'secondary.500' : 'brand.100'}
          >
            Log in
          </Link>
        </Text>
      </div>
    </>
  );
};
